import React, { useState } from 'react';
import LegalLayout from '../components/LegalLayout';
import Icon from '../components/Icon';
import api from '../lib/api';
import { usePageMeta } from '../hooks/usePageMeta';

const SUBJECTS = ['General question', 'Correction', 'Privacy request', 'Partnership'];

const CONTACT_HELPLINES = [
  'India: Emergency 112 · Tele-MANAS (mental health) 14416 · Women Helpline 181',
  'USA: 988 Suicide & Crisis Lifeline (call or text 988)',
  'Canada: 9-8-8 Suicide Crisis Helpline (call or text 9-8-8)',
  'UK: Samaritans 116 123 · NHS 111 (mental health option)',
  'Australia: Lifeline 13 11 14',
  'Elsewhere: your local emergency number or nearest hospital.',
];

const SECTIONS = [
  {
    heading: 'How to reach us',
    bullets: [
      'Email: hello@overall-wellbeing.com',
      'Published by: Mediatine Adscale Private Limited, Office no 202B, 2nd Floor, F-344/1, Tara Bhawan, Lado Sarai, New Delhi 110030, India',
      'We usually reply within 3 working days.',
    ],
  },
];

const inputCls = 'w-full h-11 px-space-md rounded-lg bg-surface-container-low border border-surface-container font-body-md text-body-md text-outline placeholder:text-on-surface-variant focus:outline-none focus:border-surface-tint transition-colors';
const labelCls = 'font-label-md text-label-md uppercase tracking-wider text-on-surface-variant mb-space-xs block';

export default function Contact() {
  usePageMeta({
    title: 'Contact us | OVERALL WELLBEING',
    description: 'Questions, corrections, ideas for a guide or partnership enquiries — here is how to reach us.',
    path: '/contact',
  });

  const [form, setForm] = useState({ name: '', email: '', subject: SUBJECTS[0], message: '' });
  const [state, setState] = useState({ sending: false, done: '', error: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ sending: true, done: '', error: '' });
    try {
      const { data } = await api.post('/contact', form);
      setState({ sending: false, done: data.message, error: '' });
      setForm({ name: '', email: '', subject: SUBJECTS[0], message: '' });
    } catch (err) {
      setState({ sending: false, done: '', error: 'We could not send your message just now. Please email us instead.' });
    }
  };

  return (
    <LegalLayout
      testId="contact-page"
      eyebrow="Get in touch"
      title="Contact us"
      intro="We’d love to hear from you, whether it’s a question, a correction, an idea for a guide or a partnership enquiry."
      sections={SECTIONS}
    >
      <form onSubmit={submit} className="mt-space-2xl p-space-xl rounded-lg bg-surface-container-low border border-surface-container" data-testid="contact-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
          <div>
            <label className={labelCls} htmlFor="contact-name">Name</label>
            <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set('name')} className={inputCls} placeholder="Your name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="contact-email">Email</label>
            <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set('email')} className={inputCls} placeholder="you@example.com" />
          </div>
        </div>
        <div className="mt-space-md">
          <label className={labelCls} htmlFor="contact-subject">Subject</label>
          <select id="contact-subject" data-testid="contact-subject-select" value={form.subject} onChange={set('subject')} className={inputCls}>
            {SUBJECTS.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div className="mt-space-md">
          <label className={labelCls} htmlFor="contact-message">Message</label>
          <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set('message')} className={`${inputCls} h-auto py-space-md`} placeholder="How can we help?" />
        </div>

        <button
          type="submit"
          data-testid="contact-submit-button"
          disabled={state.sending}
          className="mt-space-lg inline-flex items-center justify-center px-space-xl h-11 rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider shadow-[0_4px_16px_rgba(240,40,223,0.25)] hover:bg-primary transition-all disabled:opacity-60"
        >
          {state.sending ? 'Sending…' : 'Send'}
        </button>

        {state.done ? (
          <p className="mt-space-md font-body-md text-body-md text-outline" data-testid="contact-success">{state.done}</p>
        ) : null}
        {state.error ? (
          <p className="mt-space-md font-body-md text-body-md text-error" data-testid="contact-error">{state.error}</p>
        ) : null}
      </form>

      <div className="mt-space-lg p-space-lg rounded-lg bg-primary-container border-l-4 border-surface-tint" data-testid="contact-emergency-notice">
        <div className="flex items-start gap-space-md">
          <Icon name="verified" className="text-surface-tint mt-1 text-[24px] shrink-0" />
          <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
            We can’t help with medical emergencies or personal medical advice. In an emergency, call 112 in India, or
            your local emergency number. For mental-health support in India, call Tele-MANAS 14416 (free, 24×7).
            Outside India, see the helplines below.
          </p>
        </div>
      </div>

      <div className="mt-space-lg" data-testid="contact-helplines">
        <h2 className="font-headline-sm text-headline-sm text-outline uppercase tracking-tight mb-space-sm">Helplines</h2>
        <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-sm">
          We can’t help with medical emergencies or personal medical advice. If you need support now, please contact:
        </p>
        <ul className="list-disc pl-5 flex flex-col gap-space-xs">
          {CONTACT_HELPLINES.map((line) => (
            <li key={line} className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{line}</li>
          ))}
        </ul>
      </div>
    </LegalLayout>
  );
}
